import SelectInput from '../../Utils/FormInputs/SelectInput';
import TextField from '../../Utils/FormInputs/TextField';
/**
 * AnaesthesiaSimplified.js
 * Simplified anaesthesia form for local-anaesthesia-only or sedation procedures.
 * Captures local agent details, adverse reactions, and baseline vitals only.
 */
import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import Checkbox from '@material-ui/core/Checkbox';
import CircularProgress from '@material-ui/core/CircularProgress';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';
import { VitalsRow } from '../shared';
import { ANAESTHESIA_TYPE, ANAESTHESIA_TYPE_LABELS } from '../shared/constants';
import { surgeryService } from '../../../services/surgeryService';

const VOLUME_UNITS = ['ml', 'cc'];

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 24,
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#333',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: '0.875rem',
    color: '#666',
    marginBottom: 16,
  },
  section: {
    marginTop: 20,
    marginBottom: 8,
  },
  sectionTitle: {
    fontSize: '1.05rem',
    fontWeight: 600,
    color: '#333',
    marginBottom: 12,
  },
  adverseSection: {
    marginTop: 16,
    padding: 16,
    backgroundColor: '#FFF8E1',
    borderRadius: 8,
    border: '1px solid #FFE082',
  },
  vitalsSection: {
    marginTop: 16,
    padding: 16,
    backgroundColor: '#FAFAFA',
    borderRadius: 8,
  },
  vitalsLabel: {
    fontSize: '0.85rem',
    fontWeight: 500,
    color: '#666',
    marginBottom: 8,
  },
  vitalsHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 8,
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 24,
    paddingTop: 16,
    borderTop: '1px solid #e0e0e0',
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: '0.8rem',
    marginTop: 4,
  },
}));

const AnaesthesiaSimplified = ({ surgeryId }) => {
  const classes = useStyles();

  const [anaesthesiaType, setAnaesthesiaType] = useState(ANAESTHESIA_TYPE.LOCAL);
  const [localAgent, setLocalAgent] = useState('');
  const [volumeAdministered, setVolumeAdministered] = useState('');
  const [volumeUnit, setVolumeUnit] = useState('ml');
  const [siteOfInjection, setSiteOfInjection] = useState('');
  const [hasAdverseReaction, setHasAdverseReaction] = useState(false);
  const [adverseNotes, setAdverseNotes] = useState('');

  // Baseline vitals
  const [baselineVitals, setBaselineVitals] = useState({
    systolic: '',
    diastolic: '',
    heartRate: '',
    spo2: '',
    temperature: '',
    respiratoryRate: '',
  });

  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [errors, setErrors] = useState({});

  const handleVitalsChange = (field, value) => {
    if (field === 'bp') {
      const parts = value.split('/');
      setBaselineVitals((prev) => ({
        ...prev,
        systolic: parts[0] || '',
        diastolic: parts[1] || '',
      }));
    } else {
      setBaselineVitals((prev) => ({ ...prev, [field]: value }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!localAgent.trim()) newErrors.localAgent = 'Local agent is required';
    if (!volumeAdministered) newErrors.volumeAdministered = 'Volume is required';
    if (!siteOfInjection.trim()) newErrors.siteOfInjection = 'Site of injection is required';
    if (hasAdverseReaction && !adverseNotes.trim()) {
      newErrors.adverseNotes = 'Please describe the adverse reaction';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = {
        surgery_id: surgeryId,
        anaesthesia_type: anaesthesiaType,
        local_agent: localAgent,
        volume_administered: Number(volumeAdministered),
        volume_unit: volumeUnit,
        site_of_injection: siteOfInjection,
        adverse_reaction: hasAdverseReaction,
        adverse_reaction_notes: hasAdverseReaction ? adverseNotes : null,
        baseline_vitals: baselineVitals,
      };

      await surgeryService.createAnaesthesiaRecord(payload);
      setSnackbar({ open: true, message: 'Simplified anaesthesia record saved', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to save anaesthesia record', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const now = new Date();
  const timestamp = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

  return (
    <Paper className={classes.root} elevation={1}>
      <Typography className={classes.title}>Anaesthesia Record (Simplified)</Typography>
      <Typography className={classes.subtitle}>
        For local anaesthesia or sedation procedures
      </Typography>

      <Divider />

      {/* Anaesthesia Type */}
      <div className={classes.section}>
        <FormControl component="fieldset">
          <FormLabel component="legend">
            <Typography className={classes.sectionTitle}>Anaesthesia Type</Typography>
          </FormLabel>
          <RadioGroup
            row
            value={anaesthesiaType}
            onChange={(e) => setAnaesthesiaType(e.target.value)}
          >
            <FormControlLabel
              value={ANAESTHESIA_TYPE.LOCAL}
              control={<Radio color="primary" />}
              label={ANAESTHESIA_TYPE_LABELS[ANAESTHESIA_TYPE.LOCAL]}
            />
            <FormControlLabel
              value={ANAESTHESIA_TYPE.SEDATION}
              control={<Radio color="primary" />}
              label={ANAESTHESIA_TYPE_LABELS[ANAESTHESIA_TYPE.SEDATION]}
            />
          </RadioGroup>
        </FormControl>
      </div>

      {/* Agent Details */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Agent Details</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Local Agent Used"
              value={localAgent}
              onChange={(e) => {
                setLocalAgent(e.target.value);
                setErrors((prev) => ({ ...prev, localAgent: undefined }));
              }}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="e.g. Lidocaine 2%"
              error={!!errors.localAgent}
              helperText={errors.localAgent}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Volume"
              value={volumeAdministered}
              onChange={(e) => {
                setVolumeAdministered(e.target.value);
                setErrors((prev) => ({ ...prev, volumeAdministered: undefined }));
              }}
              variant="outlined"
              size="small"
              fullWidth
              type="number"
              inputProps={{ min: 0, step: 0.5 }}
              error={!!errors.volumeAdministered}
              helperText={errors.volumeAdministered}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SelectInput
              name="volumeUnit"
              value={volumeUnit}
              onChange={(e) => setVolumeUnit(e.target.value)}
              options={VOLUME_UNITS.map((unit) => ({ value: unit, text: unit }))}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Site of Injection"
              value={siteOfInjection}
              onChange={(e) => {
                setSiteOfInjection(e.target.value);
                setErrors((prev) => ({ ...prev, siteOfInjection: undefined }));
              }}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="e.g. Right inguinal region, wound edges"
              error={!!errors.siteOfInjection}
              helperText={errors.siteOfInjection}
            />
          </Grid>
        </Grid>
      </div>

      {/* Adverse Reactions */}
      <div className={classes.section}>
        <div className={hasAdverseReaction ? classes.adverseSection : undefined}>
          <FormControlLabel
            control={
              <Checkbox
                checked={hasAdverseReaction}
                onChange={(e) => {
                  setHasAdverseReaction(e.target.checked);
                  if (!e.target.checked) {
                    setAdverseNotes('');
                    setErrors((prev) => ({ ...prev, adverseNotes: undefined }));
                  }
                }}
                color="secondary"
              />
            }
            label={
              <Typography variant="body2" style={{ fontWeight: 500 }}>
                Adverse reaction occurred
              </Typography>
            }
          />
          {hasAdverseReaction && (
            <TextField
              label="Describe the adverse reaction"
              value={adverseNotes}
              onChange={(e) => {
                setAdverseNotes(e.target.value);
                setErrors((prev) => ({ ...prev, adverseNotes: undefined }));
              }}
              variant="outlined"
              size="small"
              fullWidth
              multiline
              rows={3}
              style={{ marginTop: 8 }}
              error={!!errors.adverseNotes}
              helperText={errors.adverseNotes}
              placeholder="Type of reaction, time of onset, treatment given..."
            />
          )}
        </div>
      </div>

      {/* Baseline Vitals */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Baseline Vitals</Typography>
        <div className={classes.vitalsSection}>
          <Typography className={classes.vitalsLabel}>
            Record baseline vitals before procedure
          </Typography>
          <Grid container spacing={1} style={{ marginBottom: 4 }}>
            <Grid item xs={2}>
              <Typography variant="caption" color="textSecondary">Time</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="caption" color="textSecondary">BP (Sys/Dia)</Typography>
            </Grid>
            <Grid item xs={1}>
              <Typography variant="caption" color="textSecondary">HR</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="caption" color="textSecondary">SpO2 %</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="caption" color="textSecondary">Temp</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="caption" color="textSecondary">RR</Typography>
            </Grid>
          </Grid>
          <VitalsRow
            timestamp={timestamp}
            systolic={baselineVitals.systolic}
            diastolic={baselineVitals.diastolic}
            heartRate={baselineVitals.heartRate}
            spo2={baselineVitals.spo2}
            temperature={baselineVitals.temperature}
            respiratoryRate={baselineVitals.respiratoryRate}
            onChange={handleVitalsChange}
            showDelete={false}
          />
        </div>
      </div>

      {/* Save */}
      <div className={classes.footer}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? <CircularProgress size={22} color="inherit" /> : 'Save Record'}
        </Button>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default AnaesthesiaSimplified;
